import { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ChevronDown, Calendar } from "lucide-react";

const YearRangeFilter = ({ onRangeChange }) => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = 2024; y <= currentYear; y++) years.push(y);

  const [isOpen, setIsOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(2026);
  const [isCustom, setIsCustom] = useState(false);
  const [showPickers, setShowPickers] = useState(false);
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    onRangeChange(1, 2026, 12, 2026);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setShowPickers(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleYearSelect = (year) => {
    setSelectedYear(year);
    setIsCustom(false);
    setShowPickers(false);
    setCustomStart(null);
    setCustomEnd(null);
    setIsOpen(false);
    onRangeChange(1, year, 12, year);
  };

  const handleCustomClick = () => {
    setIsCustom(true);
    setShowPickers(true);
    setCustomStart(null);
    setCustomEnd(null);
  };

  const handleApply = () => {
    if (customStart && customEnd) {
      onRangeChange(
        customStart.getMonth() + 1,
        customStart.getFullYear(),
        customEnd.getMonth() + 1,
        customEnd.getFullYear(),
      );
      setIsOpen(false);
      setShowPickers(false);
    }
  };

  const getLabel = () => {
    if (isCustom) return "Custom Range";
    return String(selectedYear);
  };

  return (
    <div className="relative inline-block w-full sm:w-auto" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between gap-2 px-3 sm:px-4 py-2 bg-primary-cyan hover:bg-primary-cyan/80 rounded-lg transition-all text-white font-medium w-full sm:w-auto text-sm sm:text-base"
      >
        <Calendar size={14} className="sm:w-4 sm:h-4" />
        <span className="truncate">{getLabel()}</span>
        <ChevronDown
          size={14}
          className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full mt-2 right-0 left-0 sm:left-auto w-full sm:w-52 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-50">
          <div className="py-2 max-h-80 overflow-y-auto">
            {years.map((year) => (
              <button
                key={year}
                onClick={() => handleYearSelect(year)}
                className={`w-full text-left px-3 sm:px-4 py-2 transition-colors text-sm sm:text-base ${
                  !isCustom && selectedYear === year
                    ? "text-primary-cyan font-semibold bg-gray-50"
                    : "text-primary-dark hover:bg-gray-50"
                }`}
              >
                {year}
              </button>
            ))}
            <div className="border-t border-gray-200 my-1" />
            <button
              onClick={handleCustomClick}
              className={`w-full text-left px-3 sm:px-4 py-2 transition-colors text-sm sm:text-base ${
                isCustom
                  ? "text-primary-cyan font-semibold bg-gray-50"
                  : "text-primary-dark hover:bg-gray-50"
              }`}
            >
              Custom Range
            </button>
          </div>

          {showPickers && (
            <div className="border-t border-gray-100 p-3 sm:p-4 bg-gray-50">
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                  Start Month
                </label>
                <DatePicker
                  selected={customStart}
                  onChange={(date) => setCustomStart(date)}
                  dateFormat="MM/yyyy"
                  showMonthYearPicker
                  maxDate={customEnd || new Date()}
                  className="w-full px-2 sm:px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-cyan/50 focus:border-primary-cyan bg-white"
                  placeholderText="Select start month"
                />
              </div>
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                  End Month
                </label>
                <DatePicker
                  selected={customEnd}
                  onChange={(date) => setCustomEnd(date)}
                  dateFormat="MM/yyyy"
                  showMonthYearPicker
                  maxDate={new Date()}
                  minDate={customStart}
                  className="w-full px-2 sm:px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-cyan/50 focus:border-primary-cyan bg-white"
                  placeholderText="Select end month"
                />
              </div>
              <button
                onClick={handleApply}
                disabled={!customStart || !customEnd}
                className="w-full bg-primary-cyan hover:bg-primary-cyan/85 text-white px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Apply Range
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default YearRangeFilter;